import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';

/**
 * A value persisted in `storage` (localStorage by default) under `key`, kept
 * in sync across every component, and every tab, that reads the same key.
 */
export function useStorageCell<V extends object>(
  key: string,
  serialize: (value: V) => string = JSON.stringify,
  deserialize: (value: string | null) => V = (value) =>
    value ? JSON.parse(value) : null,
  // eslint-disable-next-line no-storage/no-browser-storage
  storage = localStorage()
) {
  const rawValue = useSyncExternalStore(
    useCallback(
      (onStoreChange) => {
        addEventListener('storage', handleStorage);
        return () => removeEventListener('storage', handleStorage);

        function handleStorage(event: StorageEvent) {
          if (event.key === key) {
            onStoreChange();
          }
        }
      },
      [key]
    ),
    useCallback(() => {
      const value = storage.getItem(key);
      return value;
    }, [storage, key]),
    () => null
  );

  const current = {
    key,
    value: useMemo(() => deserialize(rawValue) as V, [deserialize, rawValue]),
    storage,
    serialize,
    deserialize,
  };
  const box = useRef(current);
  box.current = current;

  const setValue = useCallback(
    (update: V | ((oldValue: V) => V)) => {
      const oldValue = box.current.value;
      const newValue = box.current.serialize(
        typeof update === 'function' ? update(oldValue) : update
      );
      box.current.storage.setItem(box.current.key, newValue);
      const event = new StorageEvent('storage', {
        key: box.current.key,
        newValue,
      });
      dispatchEvent(event);
    },
    [box]
  );

  return [box.current.value, setValue] as const;
}

// eslint-disable-next-line no-storage/no-browser-storage
function localStorage(): Storage {
  try {
    // eslint-disable-next-line no-storage/no-browser-storage
    return window.localStorage;
  } catch (_) {
    return {
      getItem(key: string) {
        return this[key];
      },
      setItem(key: string, value: string) {
        this[key] = value;
      },
      removeItem(key: string) {
        delete this[key];
      },
      key(index: number) {
        return Object.keys(this)[index];
      },
      get length() {
        return Object.keys(this).length;
      },
      clear() {
        for (const key of Object.keys(this)) {
          delete this[key];
        }
      },
    };
  }
}
